/**
 * Account Setup Utility
 * Shows after tutorial completion to help users set up their account
 * Enhanced with navigation-capable onboarding agent
 */

import { getDataAPI } from '../utils/fetchData';

// Check if account setup prompt should be shown
export const shouldShowAccountSetup = () => {
    // Don't show if user hasn't completed onboarding
    const hasSeenWalkthrough = localStorage.getItem('hasSeenWalkthrough');
    const hasSeenTutorial = localStorage.getItem('hasSeenTutorial');
    
    if (!hasSeenWalkthrough || !hasSeenTutorial) {
        return false;
    }
    
    // Check if user has already completed or dismissed setup
    const setupCompleted = localStorage.getItem('accountSetupCompleted');
    if (setupCompleted) {
        return false;
    }
    
    console.log('🎯 Account Setup Check: Should show setup prompt');
    return true;
};

// Mark setup as completed
export const markSetupAsCompleted = () => {
    localStorage.setItem('accountSetupCompleted', Date.now().toString());
};

// Fetch current user state for context-aware onboarding
export const getUserState = async () => {
    try {
        // Make parallel requests to get counts
        const [bookingsRes, catalogRes, knowledgeRes, remindersRes] = await Promise.all([
            getDataAPI('user/getAllBookings').catch(() => null),
            getDataAPI('user/search-catalog').catch(() => null),
            getDataAPI('user/all-knowledge-items').catch(() => null),
            getDataAPI('user/getActiveReminders').catch(() => null)
        ]);
        
        return {
            bookingsCount: bookingsRes?.data?.bookings?.length || 0,
            catalogCount: catalogRes?.data?.items?.length || catalogRes?.data?.length || 0,
            knowledgeCount: knowledgeRes?.data?.items?.length || knowledgeRes?.data?.length || 0,
            remindersCount: remindersRes?.data?.reminders?.length || remindersRes?.data?.length || 0
        };
    } catch (error) {
        console.error('Error fetching user state:', error);
        return {
            bookingsCount: 0,
            catalogCount: 0,
            knowledgeCount: 0,
            remindersCount: 0
        };
    }
};

// Generate enhanced onboarding agent prompt with navigation capabilities
export const generateOnboardingAgentPrompt = (userState = {}) => {
    const { bookingsCount = 0, catalogCount = 0, knowledgeCount = 0, remindersCount = 0 } = userState;
    
    const hasExistingData = bookingsCount > 0 || catalogCount > 0 || knowledgeCount > 0 || remindersCount > 0;
    
    const stateContext = hasExistingData 
        ? `The user has already started exploring:
- Bookings: ${bookingsCount} ${bookingsCount === 1 ? 'show' : 'shows'}
- Catalog: ${catalogCount} ${catalogCount === 1 ? 'track' : 'tracks'}
- Knowledge items: ${knowledgeCount}
- Reminders: ${remindersCount}

Acknowledge this! Say something like "I can see you've already started exploring!" and build on what they have.`
        : `This user is brand new - no data added yet. Start from scratch with them.`;

    return `You are DIZEE's friendly onboarding agent - think of yourself as a "setup buddy" or "training camp guide" helping a new user get started.

YOUR ROLE:
- Act like a helpful friend showing someone around their new workspace
- Be encouraging, conversational, and proactive
- Guide them through the app section by section
- Use emojis naturally but not excessively (1-2 per message max)
- Keep responses concise but warm (aim for 3-5 sentences unless explaining something complex)

YOUR CAPABILITIES:
You can NAVIGATE the user through the app using these commands:
- [NAVIGATE_TO:bookings] - Takes user to Bookings
- [NAVIGATE_TO:catalog] - Takes user to Catalog  
- [NAVIGATE_TO:knowledge-base] - Takes user to Knowledge Base
- [NAVIGATE_TO:reminders] - Takes user to Reminders
- [NAVIGATE_TO:flows] - Takes user to Workflows (reminders & workflows)
- [NAVIGATE_TO:bookings/add] - Opens add booking form
- [NAVIGATE_TO:catalog/add] - Opens add catalog form
- [NAVIGATE_TO:flows/quick-add] - Opens add quick reminder form

CURRENT USER STATE:
${stateContext}

YOUR APPROACH:
1. Welcome them warmly and offer a guided tour
2. When they agree, navigate them to the first section (usually Bookings)
3. Show them around, then ask if they want to try adding something
4. Help them add an item if they want, or move to next section
5. Continue the tour through all major features
6. End with encouragement and let them know you're always available

IMPORTANT RULES:
- Always use navigation commands when moving between sections
- Ask permission before navigating ("Want me to show you Bookings?")
- After navigating, confirm where you are ("Perfect! We're in your Bookings now...")
- If they seem overwhelmed, slow down and focus on one thing
- If they have specific needs, prioritize those over the tour
- Keep it conversational - you're a buddy, not a formal assistant
- Don't use navigation commands in your first message - wait until they agree to the tour

TONE EXAMPLES:
✅ "Awesome! Let's check out your Bookings section. [NAVIGATE_TO:bookings]"
✅ "Nice! I see you already have a few tracks. Want me to show you some pro tips?"
✅ "No pressure! We can skip this and come back later. Where to next?"
❌ "I will now navigate you to the Bookings section."
❌ "Please select an option from the following list."

YOUR FIRST MESSAGE:
Start by welcoming them, acknowledging their current state, and offering a guided tour. Make it feel friendly and exciting! Don't navigate yet - just introduce yourself and offer the tour.`;
};

// Legacy prompt for backwards compatibility
export const generateSetupPrompt = () => {
    return generateOnboardingAgentPrompt();
};

// App routing map for clickable links
export const APP_ROUTES = {
    'Bookings': '/bookings',
    'bookings': '/bookings',
    'Catalog': '/catalog',
    'catalog': '/catalog',
    'Knowledge Base': '/knowledge-base',
    'knowledge base': '/knowledge-base',
    'knowledge': '/knowledge-base',
    'Reminders': '/reminders',
    'reminders': '/reminders',
    'Manager': '/bookings',
    'manager': '/bookings'
};

// Parse AI response and make app names clickable
export const parseAppLinks = (text) => {
    if (!text) return text;
    
    let processedText = text;
    
    // Replace app names with clickable versions (case-insensitive)
    Object.keys(APP_ROUTES).forEach(appName => {
        // Only match whole words (not part of other words)
        const regex = new RegExp(`\\b(${appName})\\b`, 'gi');
        processedText = processedText.replace(regex, (match) => {
            return `[APP_LINK:${match}:${APP_ROUTES[appName.toLowerCase()]}]`;
        });
    });
    
    return processedText;
};

